<script setup lang="ts">
import { ref, watch, computed, reactive, nextTick } from 'vue'
import { useDisplay } from 'vuetify'
import { format } from 'date-fns'
import { mdiMagnify, mdiFilterVariant, mdiClose } from '@mdi/js'
import type { ApplyFiltersPayload, FilterConfig, FilterValue } from '@/types'
import DateRangePicker from './DateRangePicker.vue'

// --- Props & Emits ---
interface Props {
  /** Filter definitions. Each entry must carry a unique `key`. */
  filters: FilterConfig[]
  /** Current search text. */
  search?: string
  /** Placeholder for the search field. */
  searchPlaceholder?: string
  /** Optional result count shown near the active-filter chips. */
  resultCount?: number
}

const props = withDefaults(defineProps<Props>(), {
  search: '',
  searchPlaceholder: 'Search...',
  resultCount: undefined,
})

const emit = defineEmits<{
  /** Fired when the user applies filters. Payload carries search + filter configs (with keys). */
  (e: 'apply-filters', payload: ApplyFiltersPayload): void
  /** Fired when the user resets all filters. */
  (e: 'reset'): void
}>()

// --- Responsive ---
const { smAndDown } = useDisplay()

// --- Internal pending state ---
const pendingSearchValue = ref<string>(props.search)
const pendingFilters = reactive<FilterConfig[]>([])

/**
 * Applied snapshot. Chips reflect APPLIED (not pending) values, and the Apply
 * button is disabled when pending === applied. Seeded from the incoming props
 * since the parent owns the authoritative applied state.
 */
const appliedSearchValue = ref<string>(props.search)
const appliedFilters = reactive<FilterConfig[]>([])

/**
 * Deep clone filter configs without mangling `Date` instances.
 * `JSON.parse(JSON.stringify(...))` turns Dates into strings; `structuredClone`
 * throws `DataCloneError` on values it cannot clone (functions, refs, proxies).
 * This Date-aware recursive clone handles both safely.
 */
const deepCopyFilters = (filters: FilterConfig[]): FilterConfig[] => {
  const cloneValue = (value: unknown): unknown => {
    if (value instanceof Date) return new Date(value.getTime())
    if (Array.isArray(value)) return value.map(cloneValue)
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, cloneValue(v)]),
      )
    }
    return value
  }
  return cloneValue(filters) as FilterConfig[]
}

// Keep pendingFilters + appliedFilters in sync with incoming props.
watch(
  () => props.filters,
  (newFilters) => {
    if (JSON.stringify(newFilters) !== JSON.stringify(pendingFilters)) {
      pendingFilters.splice(0, pendingFilters.length, ...deepCopyFilters(newFilters))
    }
    if (JSON.stringify(newFilters) !== JSON.stringify(appliedFilters)) {
      appliedFilters.splice(0, appliedFilters.length, ...deepCopyFilters(newFilters))
    }
  },
  { immediate: true, deep: true },
)

// Keep pendingSearchValue + appliedSearchValue in sync with the prop.
watch(
  () => props.search,
  (newSearch) => {
    if (newSearch !== pendingSearchValue.value) {
      pendingSearchValue.value = newSearch
    }
    if (newSearch !== appliedSearchValue.value) {
      appliedSearchValue.value = newSearch
    }
  },
  { immediate: true },
)

// --- Change tracking ---
const hasPendingChanges = computed(() => {
  const appliedFiltersString = JSON.stringify(appliedFilters.map((f) => f.value))
  const pendingFiltersString = JSON.stringify(pendingFilters.map((f) => f.value))
  return (
    pendingSearchValue.value !== appliedSearchValue.value ||
    appliedFiltersString !== pendingFiltersString
  )
})

// --- Active (applied) filter detection ---
// A filter is "active" when its applied value is non-empty.
const isFilterActive = (filter: FilterConfig): boolean => {
  const value = filter.value
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) {
    return value.some((entry) => entry !== null && entry !== undefined && entry !== '')
  }
  return true
}

// Date filters always render first — consistent placement on every page,
// regardless of the order a page declares its FilterConfig[]. Stable sort
// keeps all other filters in their declared order.
const dateFirst = (a: FilterConfig, b: FilterConfig): number =>
  Number(a.type !== 'date') - Number(b.type !== 'date')

// Display order for the filter inputs. `index` is the real index into
// pendingFilters so the update/clear handlers stay correct.
const displayFilters = computed(() =>
  pendingFilters
    .map((filter, index) => ({ filter, index }))
    .sort((a, b) => dateFirst(a.filter, b.filter)),
)

const activeFilters = computed(() =>
  appliedFilters.filter(isFilterActive).sort(dateFirst),
)
const activeFilterCount = computed(() => activeFilters.value.length)
const hasActiveFilters = computed(
  () => activeFilterCount.value > 0 || appliedSearchValue.value !== '',
)
const hasActiveFilterChips = computed(() => activeFilterCount.value > 0)

// --- Chip label formatting ---
const formatChipDate = (value: Date | string | number | null): string => {
  if (value === null || value === undefined || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return format(date, 'dd MMM yyyy')
}

const formatChipValue = (filter: FilterConfig): string => {
  const value = filter.value
  if (filter.type === 'date') {
    const range = Array.isArray(value) ? value : [null, null]
    const from = formatChipDate(range[0] as Date | string | number | null)
    const to = formatChipDate(range[1] as Date | string | number | null)
    if (from && to) return `${from} – ${to}`
    return from || to
  }
  if (filter.type === 'select') {
    const selected = Array.isArray(value) ? (value as string[]) : []
    const labels = selected.map((entry) => {
      const option = filter.options?.find((opt) => opt.value === entry)
      return option ? option.text : String(entry)
    })
    return labels.join(', ')
  }
  if (filter.type === 'number') {
    const range = Array.isArray(value) ? value : [null, null]
    const prefix = filter.prefix ?? ''
    const suffix = filter.suffix ?? ''
    const wrap = (entry: unknown): string =>
      entry === null || entry === undefined || entry === '' ? '' : `${prefix}${entry}${suffix}`
    const min = wrap(range[0])
    const max = wrap(range[1])
    if (min && max) return `${min} – ${max}`
    if (min) return `≥ ${min}`
    if (max) return `≤ ${max}`
    return ''
  }
  return value === null || value === undefined ? '' : String(value)
}

const chipText = (filter: FilterConfig): string => `${filter.label}: ${formatChipValue(filter)}`

// --- Date validation ---
// Clamp a date to "not in the future"; today is the latest allowed value.
const validateDate = (date: Date | null): Date | null => {
  if (!date) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date > today ? new Date(today) : date
}

// --- Pending state update handlers ---
const updatePendingSearch = (value: string | null): void => {
  pendingSearchValue.value = value ?? ''
}

const updatePendingFilter = (index: number, value: FilterValue): void => {
  const filter = pendingFilters[index]
  if (!filter) return

  if (filter.type === 'date' && Array.isArray(value)) {
    filter.value = value.map((entry) =>
      entry instanceof Date ? validateDate(entry) : entry,
    ) as FilterValue
  } else {
    filter.value = value
  }
}

// Reset a single filter (in any config list) to its empty value.
const emptyFilterValue = (filter: FilterConfig): FilterValue => {
  if (filter.type === 'select') return []
  if (filter.type === 'date' || filter.type === 'number') return [null, null]
  return null
}

const clearPendingFilter = (index: number): void => {
  const filter = pendingFilters[index]
  if (!filter) return
  filter.value = emptyFilterValue(filter)
}

// --- Apply / reset / chip removal ---
const syncAppliedFromPending = (): void => {
  appliedSearchValue.value = pendingSearchValue.value
  appliedFilters.splice(0, appliedFilters.length, ...deepCopyFilters(pendingFilters))
}

const applyFilters = (): void => {
  syncAppliedFromPending()
  emit('apply-filters', {
    search: pendingSearchValue.value,
    filters: deepCopyFilters(pendingFilters),
  })
}

const removeFilterChip = (key: string): void => {
  // Clear the key in both pending and applied state.
  const pendingIndex = pendingFilters.findIndex((f) => f.key === key)
  if (pendingIndex !== -1) {
    pendingFilters[pendingIndex].value = emptyFilterValue(pendingFilters[pendingIndex])
  }
  const appliedIndex = appliedFilters.findIndex((f) => f.key === key)
  if (appliedIndex !== -1) {
    appliedFilters[appliedIndex].value = emptyFilterValue(appliedFilters[appliedIndex])
  }
  emit('apply-filters', {
    search: pendingSearchValue.value,
    filters: deepCopyFilters(pendingFilters),
  })
}

const handleReset = (): void => {
  pendingSearchValue.value = ''
  appliedSearchValue.value = ''
  pendingFilters.forEach((_filter, index) => clearPendingFilter(index))
  appliedFilters.forEach((filter) => {
    filter.value = emptyFilterValue(filter)
  })
  emit('reset')
}

// --- Mobile disclosure panel ---
const panelId = 'standard-filter-panel'
const panelOpen = ref<boolean>(false)
const toggleButtonRef = ref<HTMLElement | null>(null)
const panelApplyRef = ref<HTMLElement | null>(null)

const togglePanel = async (): Promise<void> => {
  panelOpen.value = !panelOpen.value
  if (panelOpen.value) {
    await nextTick()
    const target = panelApplyRef.value as { $el?: HTMLElement } | HTMLElement | null
    const el = (target && '$el' in target ? target.$el : target) as HTMLElement | undefined
    el?.focus?.()
  }
}

const applyFromPanel = (): void => {
  applyFilters()
  panelOpen.value = false
  const target = toggleButtonRef.value as { $el?: HTMLElement } | HTMLElement | null
  const el = (target && '$el' in target ? target.$el : target) as HTMLElement | undefined
  el?.focus?.()
}

const resetFromPanel = (): void => {
  handleReset()
  panelOpen.value = false
}

// Apply on Enter in the search field — quick search without per-keystroke requests.
const handleSearchEnter = (): void => {
  applyFilters()
}
</script>

<template>
  <div class="standard-filter">
    <!-- Toolbar row -->
    <div class="standard-filter__toolbar">
      <!-- Search field — leftmost, widest -->
      <v-text-field
        :model-value="pendingSearchValue"
        :placeholder="searchPlaceholder || 'Search...'"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        :prepend-inner-icon="mdiMagnify"
        class="standard-filter__search filter-field"
        @update:model-value="updatePendingSearch($event)"
        @click:clear="updatePendingSearch('')"
        @keyup.enter="handleSearchEnter"
      />

      <!-- Mobile: collapse filters behind a disclosure button -->
      <template v-if="smAndDown">
        <v-btn
          v-if="pendingFilters.length > 0"
          ref="toggleButtonRef"
          variant="tonal"
          color="primary"
          class="filter-action-btn"
          :prepend-icon="mdiFilterVariant"
          :aria-expanded="panelOpen"
          :aria-controls="panelId"
          @click="togglePanel"
        >
          Filters{{ activeFilterCount > 0 ? ` (${activeFilterCount})` : '' }}
        </v-btn>
      </template>

      <!-- Desktop: inline filter fields -->
      <template v-else>
        <template v-for="{ filter, index } in displayFilters" :key="filter.key">
          <!-- Date Range Filter -->
          <div v-if="filter.type === 'date'" class="standard-filter__field filter-wrapper">
            <DateRangePicker
              :model-value="(pendingFilters[index].value as [Date | string | null, Date | string | null] | null) || null"
              :label="filter.label"
              :min-date="filter.min ? new Date(filter.min) : undefined"
              :max-date="new Date()"
              @update:model-value="(value) => updatePendingFilter(index, value)"
            />
          </div>

          <!-- Select Filter -->
          <v-select
            v-else-if="filter.type === 'select'"
            :model-value="filter.value"
            :items="filter.options || []"
            item-title="text"
            item-value="value"
            :label="filter.label"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            chips
            multiple
            class="standard-filter__field filter-field"
            @update:model-value="updatePendingFilter(index, $event)"
            @click:clear="updatePendingFilter(index, [])"
          />

          <!-- Number Range Filter -->
          <div v-else-if="filter.type === 'number'" class="standard-filter__field standard-filter__number">
            <v-text-field
              :model-value="(filter.value as (number | null)[] | null)?.[0]"
              :label="`Min ${filter.label}`"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :prefix="filter.prefix"
              :suffix="filter.suffix"
              type="number"
              class="filter-field"
              @update:model-value="updatePendingFilter(index, [$event ? Number($event) : null, (filter.value as (number | null)[] | null)?.[1] ?? null])"
              @click:clear="updatePendingFilter(index, [null, (filter.value as (number | null)[] | null)?.[1] ?? null])"
            />
            <v-text-field
              :model-value="(filter.value as (number | null)[] | null)?.[1]"
              :label="`Max ${filter.label}`"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :prefix="filter.prefix"
              :suffix="filter.suffix"
              type="number"
              class="filter-field"
              @update:model-value="updatePendingFilter(index, [(filter.value as (number | null)[] | null)?.[0] ?? null, $event ? Number($event) : null])"
              @click:clear="updatePendingFilter(index, [(filter.value as (number | null)[] | null)?.[0] ?? null, null])"
            />
          </div>
        </template>

        <v-spacer />

        <!-- Reset — subtle, visible only when there is at least one active filter -->
        <v-btn
          v-if="hasActiveFilters"
          variant="text"
          color="medium-emphasis"
          class="filter-action-btn"
          @click="handleReset"
        >
          Reset
        </v-btn>

        <!-- Apply — primary, disabled when there are no pending changes -->
        <v-btn
          color="primary"
          variant="flat"
          class="filter-action-btn"
          :disabled="!hasPendingChanges"
          @click="applyFilters"
        >
          Apply
        </v-btn>
      </template>
    </div>

    <!-- Mobile disclosure panel -->
    <v-expand-transition>
      <div v-if="smAndDown && panelOpen" :id="panelId" class="standard-filter__panel">
        <template v-for="{ filter, index } in displayFilters" :key="filter.key">
          <div v-if="filter.type === 'date'" class="standard-filter__panel-field filter-wrapper">
            <DateRangePicker
              :model-value="(pendingFilters[index].value as [Date | string | null, Date | string | null] | null) || null"
              :label="filter.label"
              :min-date="filter.min ? new Date(filter.min) : undefined"
              :max-date="new Date()"
              @update:model-value="(value) => updatePendingFilter(index, value)"
            />
          </div>

          <v-select
            v-else-if="filter.type === 'select'"
            :model-value="filter.value"
            :items="filter.options || []"
            item-title="text"
            item-value="value"
            :label="filter.label"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            chips
            multiple
            class="standard-filter__panel-field filter-field"
            @update:model-value="updatePendingFilter(index, $event)"
            @click:clear="updatePendingFilter(index, [])"
          />

          <div v-else-if="filter.type === 'number'" class="standard-filter__panel-field standard-filter__number">
            <v-text-field
              :model-value="(filter.value as (number | null)[] | null)?.[0]"
              :label="`Min ${filter.label}`"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :prefix="filter.prefix"
              :suffix="filter.suffix"
              type="number"
              class="filter-field"
              @update:model-value="updatePendingFilter(index, [$event ? Number($event) : null, (filter.value as (number | null)[] | null)?.[1] ?? null])"
              @click:clear="updatePendingFilter(index, [null, (filter.value as (number | null)[] | null)?.[1] ?? null])"
            />
            <v-text-field
              :model-value="(filter.value as (number | null)[] | null)?.[1]"
              :label="`Max ${filter.label}`"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              :prefix="filter.prefix"
              :suffix="filter.suffix"
              type="number"
              class="filter-field"
              @update:model-value="updatePendingFilter(index, [(filter.value as (number | null)[] | null)?.[0] ?? null, $event ? Number($event) : null])"
              @click:clear="updatePendingFilter(index, [(filter.value as (number | null)[] | null)?.[0] ?? null, null])"
            />
          </div>
        </template>

        <div class="standard-filter__panel-actions">
          <v-btn
            v-if="hasActiveFilters"
            variant="text"
            color="medium-emphasis"
            class="filter-action-btn"
            @click="resetFromPanel"
          >
            Reset
          </v-btn>
          <v-btn
            ref="panelApplyRef"
            color="primary"
            variant="flat"
            class="filter-action-btn"
            :disabled="!hasPendingChanges"
            @click="applyFromPanel"
          >
            Apply
          </v-btn>
        </div>
      </div>
    </v-expand-transition>

    <!-- Active-filter chips row -->
    <div v-if="hasActiveFilterChips" class="standard-filter__chips">
      <v-chip
        v-for="filter in activeFilters"
        :key="filter.key"
        size="small"
        variant="tonal"
        color="primary"
        closable
        :close-icon="mdiClose"
        :aria-label="`Remove filter: ${filter.label} — ${formatChipValue(filter)}`"
        @click:close="removeFilterChip(filter.key)"
      >
        {{ chipText(filter) }}
      </v-chip>

      <v-btn
        variant="text"
        size="small"
        color="medium-emphasis"
        class="filter-action-btn"
        @click="handleReset"
      >
        Clear all
      </v-btn>

      <v-spacer />

      <span
        v-if="resultCount !== undefined"
        class="standard-filter__count text-medium-emphasis"
        aria-live="polite"
      >
        {{ resultCount }} {{ resultCount === 1 ? 'result' : 'results' }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.standard-filter {
  margin-bottom: 16px;
}

/* Light toolbar — subtle border, no elevation, reduced padding. */
.standard-filter__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background-color: rgb(var(--v-theme-surface));
}

.standard-filter__search {
  flex: 2 1 240px;
  min-width: 200px;
}

.standard-filter__field {
  flex: 1 1 180px;
  min-width: 170px;
}

.standard-filter__number {
  display: flex;
  gap: 8px;
}

.standard-filter__number .filter-field {
  flex: 1 1 0;
}

.filter-field {
  border-radius: 8px;
}

.filter-action-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.3px;
}

/* Mobile disclosure panel. */
.standard-filter__panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background-color: rgb(var(--v-theme-surface));
}

.standard-filter__panel-field {
  width: 100%;
}

.standard-filter__panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Active-filter chips row. */
.standard-filter__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.standard-filter__count {
  font-size: 0.875rem;
  white-space: nowrap;
}
</style>
